import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Package, ChevronDown, ChevronUp, Search } from 'lucide-react';
import * as imageService from '../services/imageService';
import registryService from '../services/registryService';

const ImageFormModal = ({ image, onClose, onSave, prefill }) => {
  const isEdit = !!image;
  const defaults = image || prefill || {};
  const [name, setName] = useState(defaults.name || '');
  const [description, setDescription] = useState(defaults.description || '');
  const [dockerImage, setDockerImage] = useState(defaults.docker_image || '');
  const [visibility, setVisibility] = useState(defaults.visibility || 'private');
  const [registryServer, setRegistryServer] = useState(defaults.registry_server || '');
  const [registryUsername, setRegistryUsername] = useState(defaults.registry_username || '');
  const [registryPassword, setRegistryPassword] = useState(defaults.registry_password || '');
  const [showRegistry, setShowRegistry] = useState(!!(defaults.registry_server));
  const [pricingRates, setPricingRates] = useState(defaults.pricing_rates || {});
  const [showPricing, setShowPricing] = useState(!!(defaults.pricing_rates && Object.keys(defaults.pricing_rates).length > 0));
  const [skipWeightExtraction, setSkipWeightExtraction] = useState(
    !!defaults.skip_weight_extraction);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Default resources state
  const defaultRes = defaults.default_resources || {};
  const [defaultResources, setDefaultResources] = useState(defaultRes);
  const [showResources, setShowResources] = useState(!!(defaultRes && Object.keys(defaultRes).length > 0));

  // Exposed ports state (frp tunnel defaults)
  const defaultPorts = Array.isArray(defaults.exposed_ports) ? defaults.exposed_ports : [];
  const [exposedPorts, setExposedPorts] = useState(defaultPorts);
  const [showPorts, setShowPorts] = useState(defaultPorts.length > 0);
  const [acceleratorList, setAcceleratorList] = useState([]);
  const [loadingAccelerators, setLoadingAccelerators] = useState(false);
  const [gpuSearch, setGpuSearch] = useState('');
  const [gpuDropdownOpen, setGpuDropdownOpen] = useState(false);
  const gpuDropdownRef = useRef(null);
  const [savedRegistries, setSavedRegistries] = useState([]);
  const [registryAutoFilled, setRegistryAutoFilled] = useState(null);

  // Fetch user's saved registries for auto-fill
  useEffect(() => {
    registryService.listRegistries()
      .then(data => { if (data.success) setSavedRegistries(data.registries || []); })
      .catch(() => {});
  }, []);

  // Extract registry server from a Docker image URL.
  // Returns the hostname if it contains a dot (non-Docker-Hub), null otherwise.
  const extractRegistryServer = useCallback((image) => {
    if (!image) return null;
    const firstSegment = image.split('/')[0];
    if (firstSegment.includes('.') || firstSegment.includes(':')) return firstSegment;
    return null;
  }, []);

  // Auto-detect registry from Docker image URL and fill from saved registries
  useEffect(() => {
    if (isEdit) return; // don't override on edit
    const server = extractRegistryServer(dockerImage);
    if (!server) {
      if (registryAutoFilled) {
        setRegistryAutoFilled(null);
      }
      return;
    }
    // Auto-fill registry server
    if (!registryServer || registryServer !== server) {
      setRegistryServer(server);
    }
    // Find matching saved registry and auto-fill credentials
    const match = savedRegistries.find(r => server.startsWith(r.server) || r.server.startsWith(server));
    if (match && !registryPassword) {
      setRegistryUsername(match.username || '');
      setShowRegistry(true);
      setRegistryAutoFilled(match.name);
    }
  }, [dockerImage, savedRegistries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch accelerators when resources section is expanded
  useEffect(() => {
    if (showResources && acceleratorList.length === 0) {
      setLoadingAccelerators(true);
      imageService.listAccelerators()
        .then(data => { if (data.success) setAcceleratorList(data.accelerators || []); })
        .catch(() => {})
        .finally(() => setLoadingAccelerators(false));
    }
  }, [showResources]);

  // Close GPU dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (gpuDropdownRef.current && !gpuDropdownRef.current.contains(e.target)) {
        setGpuDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const updateResource = (key, value) => {
    setDefaultResources(prev => {
      const next = { ...prev };
      if (value === '' || value === undefined || value === null) {
        delete next[key];
      } else {
        next[key] = typeof value === 'number' ? value : value;
      }
      return next;
    });
  };

  const filteredGpus = acceleratorList.filter(g =>
    g.toLowerCase().includes(gpuSearch.toLowerCase())
  );

  const updateRate = (key, value) => {
    setPricingRates(prev => {
      const next = { ...prev };
      if (value === '' || value === undefined) {
        delete next[key];
      } else {
        next[key] = parseFloat(value);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !dockerImage.trim()) {
      setError('Name and Docker image are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const cleanedRates = Object.keys(pricingRates).length > 0 ? pricingRates : undefined;
      const cleanedResources = Object.keys(defaultResources).length > 0 ? defaultResources : undefined;
      // Keep only rows with a port; normalize port to a number
      const cleanedPorts = exposedPorts
        .filter(p => p.port !== '' && p.port !== undefined && p.port !== null)
        .map(p => ({
          port: parseInt(p.port, 10),
          protocol: p.protocol || 'http',
          name: (p.name || '').trim(),
          ...(p.auth ? { auth: p.auth.trim() } : {}),
        }));
      await onSave({
        name, description, dockerImage, visibility,
        registryServer: registryServer || undefined,
        registryUsername: registryUsername || undefined,
        registryPassword: registryPassword || undefined,
        pricingRates: cleanedRates,
        defaultResources: cleanedResources,
        exposedPorts: cleanedPorts,
        skipWeightExtraction,
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex justify-between items-center rounded-t-lg">
          <div className="flex items-center space-x-3 text-white">
            <Package size={20} />
            <h2 className="text-lg font-bold">{isEdit ? 'Edit Image' : 'Add Curated Image'}</h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. PyTorch Training"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the image..."
              rows="2"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Docker Image *</label>
            <input
              type="text"
              value={dockerImage}
              onChange={(e) => setDockerImage(e.target.value)}
              placeholder="e.g. rg.fr-par.scw.cloud/ns/image:tag"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="private">Private</option>
              <option value="public">Public</option>
              <option value="semi_private">Shared (Semi-Private)</option>
            </select>
          </div>

          {/* Weight handling at conversion time */}
          <div className="border border-gray-200 rounded-lg px-3 py-2">
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={skipWeightExtraction}
                onChange={(e) => setSkipWeightExtraction(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-700">
                  Keep model weights inside the image
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  By default conversion moves files over 100&nbsp;MB to shared storage and
                  fetches them at startup, which keeps the image small and shares
                  weights between images. Tick this for models that load their
                  weights from a fixed path and break when the files move — the
                  image stays larger and nothing is de-duplicated. Takes effect
                  on the next conversion.
                </span>
              </span>
            </label>
          </div>

          {/* Registry Authentication (collapsible) */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowRegistry(!showRegistry)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <span>Registry Authentication (for private registries)</span>
              {showRegistry ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {showRegistry && (
              <div className="px-3 pb-3 space-y-3 border-t border-gray-100 pt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Registry Server</label>
                  <input
                    type="text"
                    value={registryServer}
                    onChange={(e) => setRegistryServer(e.target.value)}
                    placeholder="e.g. rg.fr-par.scw.cloud"
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Username</label>
                  <input
                    type="text"
                    value={registryUsername}
                    onChange={(e) => setRegistryUsername(e.target.value)}
                    placeholder="e.g. nologin"
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Password / Secret Key</label>
                  <input
                    type="password"
                    value={registryPassword}
                    onChange={(e) => setRegistryPassword(e.target.value)}
                    placeholder={isEdit && image?.registry_password ? '(unchanged)' : 'Enter secret key'}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                {registryAutoFilled && (
                  <p className="text-xs text-green-600 font-medium">
                    Server and username auto-filled from saved registry "{registryAutoFilled}". Password will be inherited from it at launch time.
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  Used for private registries like Scaleway, AWS ECR, GCR, etc. Credentials are inherited from your saved registries (Settings) if not provided here.
                </p>
              </div>
            )}
          </div>

          {/* Pricing Rates (collapsible) */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowPricing(!showPricing)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <span>Pricing Rates (billing configuration)</span>
              {showPricing ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {showPricing && (
              <div className="px-3 pb-3 space-y-3 border-t border-gray-100 pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Per Server Hour ($)</label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={pricingRates.per_server_hour ?? ''}
                      onChange={(e) => updateRate('per_server_hour', e.target.value)}
                      placeholder="e.g. 2.50"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Per Inference Run ($)</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={pricingRates.per_inference_run ?? ''}
                      onChange={(e) => updateRate('per_inference_run', e.target.value)}
                      placeholder="e.g. 0.01"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Per Inference Minute ($)</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={pricingRates.per_inference_minute ?? ''}
                      onChange={(e) => updateRate('per_inference_minute', e.target.value)}
                      placeholder="e.g. 0.05"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Per Input MB ($)</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={pricingRates.per_input_mb ?? ''}
                      onChange={(e) => updateRate('per_input_mb', e.target.value)}
                      placeholder="e.g. 0.001"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="w-1/2 pr-1.5">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Per Output MB ($)</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={pricingRates.per_output_mb ?? ''}
                    onChange={(e) => updateRate('per_output_mb', e.target.value)}
                    placeholder="e.g. 0.002"
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Set billing rates for this image. Rates are inherited by clusters at launch time. Leave blank for rates that don't apply.
                </p>
              </div>
            )}
          </div>

          {/* Default Resources (collapsible) */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowResources(!showResources)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <span>Default Resources (auto-populate at launch)</span>
              {showResources ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {showResources && (
              <div className="px-3 pb-3 space-y-3 border-t border-gray-100 pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Min CPU Cores</label>
                    <input
                      type="number"
                      min="1"
                      max="128"
                      value={defaultResources.cpus ?? ''}
                      onChange={(e) => updateResource('cpus', e.target.value ? parseInt(e.target.value) : '')}
                      placeholder="e.g. 4"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Min RAM (GB)</label>
                    <input
                      type="number"
                      min="1"
                      max="1024"
                      value={defaultResources.memory ?? ''}
                      onChange={(e) => updateResource('memory', e.target.value ? parseInt(e.target.value) : '')}
                      placeholder="e.g. 16"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div ref={gpuDropdownRef} className="relative">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Accelerator (GPU)</label>
                    <div
                      onClick={() => setGpuDropdownOpen(!gpuDropdownOpen)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm cursor-pointer flex items-center justify-between hover:border-gray-400"
                    >
                      <span className={defaultResources.accelerator ? 'text-gray-900' : 'text-gray-400'}>
                        {defaultResources.accelerator || 'Select GPU...'}
                      </span>
                      <ChevronDown size={12} className="text-gray-400" />
                    </div>

                    {gpuDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <div className="flex items-center border border-gray-300 rounded px-2 py-1">
                            <Search size={12} className="text-gray-400 mr-1" />
                            <input
                              type="text"
                              value={gpuSearch}
                              onChange={(e) => setGpuSearch(e.target.value)}
                              placeholder="Search GPUs..."
                              className="w-full text-xs outline-none"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-36 overflow-y-auto">
                          {loadingAccelerators ? (
                            <div className="p-2 text-xs text-gray-500 text-center">Loading...</div>
                          ) : filteredGpus.length === 0 ? (
                            <div className="p-2 text-xs text-gray-500 text-center">No matches</div>
                          ) : (
                            <>
                              {defaultResources.accelerator && (
                                <button
                                  type="button"
                                  onClick={() => { updateResource('accelerator', ''); updateResource('accelerator_count', ''); setGpuDropdownOpen(false); setGpuSearch(''); }}
                                  className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-50 italic"
                                >
                                  Clear selection
                                </button>
                              )}
                              {filteredGpus.map(gpu => (
                                <button
                                  key={gpu}
                                  type="button"
                                  onClick={() => {
                                    updateResource('accelerator', gpu);
                                    if (!defaultResources.accelerator_count) updateResource('accelerator_count', 1);
                                    setGpuDropdownOpen(false);
                                    setGpuSearch('');
                                  }}
                                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-purple-50 ${
                                    defaultResources.accelerator === gpu ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700'
                                  }`}
                                >
                                  {gpu}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {defaultResources.accelerator && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">GPU Count</label>
                      <input
                        type="number"
                        min="1"
                        max="8"
                        value={defaultResources.accelerator_count ?? 1}
                        onChange={(e) => updateResource('accelerator_count', e.target.value ? parseInt(e.target.value) : '')}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>

                <div className="w-1/2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Disk Size (GB)</label>
                  <input
                    type="number"
                    min="50"
                    max="2000"
                    value={defaultResources.disk_size ?? ''}
                    onChange={(e) => updateResource('disk_size', e.target.value ? parseInt(e.target.value) : '')}
                    placeholder="e.g. 200"
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <p className="text-xs text-gray-500">
                  These defaults auto-populate the launch form when a user selects this image. Leave blank for fields that shouldn't be pre-filled.
                </p>
              </div>
            )}
          </div>

          {/* Exposed Ports (collapsible) */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowPorts(!showPorts)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <span>Exposed Ports (tunnel defaults)</span>
              {showPorts ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {showPorts && (
              <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-3">
                {exposedPorts.map((p, i) => {
                  const upd = (patch) => setExposedPorts(prev => prev.map((x, j) => j === i ? { ...x, ...patch } : x));
                  const isRaw = p.protocol === 'tcp' || p.protocol === 'udp';
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="number"
                        placeholder="Port"
                        value={p.port ?? ''}
                        onChange={(e) => upd({ port: e.target.value })}
                        className="col-span-3 border border-gray-300 rounded px-2 py-1.5 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <select
                        value={p.protocol || 'http'}
                        onChange={(e) => upd({ protocol: e.target.value })}
                        className="col-span-2 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="http">http</option>
                        <option value="https">https</option>
                        <option value="tcp">tcp</option>
                        <option value="udp">udp</option>
                      </select>
                      <input
                        placeholder="name"
                        value={p.name || ''}
                        onChange={(e) => upd({ name: e.target.value })}
                        className="col-span-4 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <input
                        placeholder={isRaw ? 'remote port (opt)' : 'user:pass (opt)'}
                        value={p.auth || ''}
                        onChange={(e) => upd({ auth: e.target.value })}
                        className="col-span-2 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setExposedPorts(prev => prev.filter((_, j) => j !== i))}
                        className="col-span-1 text-gray-400 hover:text-red-600 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setExposedPorts(prev => [...prev, { port: '', protocol: 'http', name: '', auth: '' }])}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  + Add port
                </button>
                <p className="text-xs text-gray-500">
                  Pre-fills the launch form's exposed ports. For http/https, the
                  auth field sets basic-auth as <code>user:pass</code>; for
                  tcp/udp it sets an optional fixed remote port.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImageFormModal;
